import React, { useCallback, useMemo } from 'react';
import { ByMetricTrendsConfig } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { ByMetricTrendsGroupByConfiguration } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { ByMetricTrendsMetricBucketingStrategy } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { byMetricTrendsMetricBucketingStrategyDropdownValues } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { ByMetricTrendsMetricConfiguration } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

type MetricConfigViewModel = {
  metric: string;
  isEnabled: boolean;
  errorMessages: string[];
  availableGroupBys: string[];
  bucketingStrategy: ByMetricTrendsMetricBucketingStrategy | null;
};

type AvailableGroupByViewModel = {
  key: string;
  displayName: string;
  isEnabled: boolean;
  errorMessages: string[];
};

export const ByMetricTrendsConfigurationAvailableGroupBysComponent = ({
  groupByConfigs,
  availableGroupBys,
  setAvailableGroupBys,
}: {
  groupByConfigs: ByMetricTrendsGroupByConfiguration[];
  availableGroupBys: string[];
  setAvailableGroupBys: Setter<string[]>;
}) => {
  const viewModels: AvailableGroupByViewModel[] = useMemo(() => {
    const currentGroupBys: AvailableGroupByViewModel[] = availableGroupBys.map(
      (groupBy) => {
        const foundConfig = groupByConfigs.find(
          (availableGroupBy) => availableGroupBy.key === groupBy,
        );

        if (foundConfig && groupBy !== '') {
          return {
            key: groupBy,
            displayName: foundConfig.displayName,
            isEnabled: true,
            errorMessages: [],
          };
        } else {
          return {
            key: groupBy,
            displayName: groupBy,
            isEnabled: true,
            errorMessages: [`Group by ${groupBy} not found in configs.`],
          };
        }
      },
    );

    const missingGroupBys: AvailableGroupByViewModel[] = groupByConfigs
      .filter((groupBy) => !currentGroupBys.some((c) => c.key === groupBy.key))
      .map((groupBy) => ({
        key: groupBy.key,
        displayName: groupBy.displayName,
        isEnabled: false,
        errorMessages: [],
      }));

    return [...currentGroupBys, ...missingGroupBys].sort((a, b) =>
      a.key.localeCompare(b.key),
    );
  }, [groupByConfigs, availableGroupBys]);

  const setViewModels = useCallback(
    (newViewModels: AvailableGroupByViewModel[]) => {
      const newAvailableGroupBys = newViewModels
        .filter((viewModel) => viewModel.isEnabled)
        .map((viewModel) => viewModel.key);

      setAvailableGroupBys([...newAvailableGroupBys]);
    },
    [setAvailableGroupBys],
  );

  const crudOperations = useCrudOperations({
    items: viewModels,
    setItems: setViewModels,
  });

  const onSelectAll = useCallback(() => {
    const allEnabled = viewModels.every((viewModel) => viewModel.isEnabled);
    const newViewModels = viewModels.map((viewModel) => ({
      ...viewModel,
      isEnabled: !allEnabled,
    }));
    setViewModels(newViewModels);
  }, [viewModels, setViewModels]);

  const onDeselectAll = useCallback(() => {
    const newViewModels = viewModels.map((viewModel) => ({
      ...viewModel,
      isEnabled: false,
    }));
    setViewModels(newViewModels);
  }, [viewModels, setViewModels]);

  return (
    <Spacing size={'sm'} direction={'vertical'}>
      <Spacing size={'sm'} direction={'horizontal'}>
        <TouchTarget
          content={() => {
            return (
              <S2Typography
                variant={'headline-7-alt'}
                translatedContent={'Select All'}
                textColorVariant={'royal-600'}
              />
            );
          }}
          behavior={{
            type: 'onClick',
            onClick: onSelectAll,
          }}
        />

        <TouchTarget
          content={() => {
            return (
              <S2Typography
                variant={'headline-7-alt'}
                translatedContent={'Deselect All'}
                textColorVariant={'red-600'}
              />
            );
          }}
          behavior={{
            type: 'onClick',
            onClick: onDeselectAll,
          }}
        />
      </Spacing>
      {viewModels.map((viewModel, index) => (
        <S2Checkbox
          key={index}
          checked={viewModel.isEnabled}
          onChange={() => {
            crudOperations.onEdit(index, {
              isEnabled: !viewModel.isEnabled,
            });
          }}
        >
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={viewModel.displayName}
          />
        </S2Checkbox>
      ))}
    </Spacing>
  );
};

export const ByMetricTrendsBucketingStrategyComponent = ({
  possibleDateColumns,
  strategy,
  setStrategy,
}: {
  possibleDateColumns: string[];
  strategy: ByMetricTrendsMetricBucketingStrategy;
  setStrategy: Setter<ByMetricTrendsMetricBucketingStrategy>;
}) => {
  const possibleDateColumnsDropdowns = useMemo(() => {
    return possibleDateColumns.map((column) => ({
      optionLabel: column,
      optionValue: column,
    }));
  }, [possibleDateColumns]);
  const detailsUi = useMemo(() => {
    if (strategy.type === 'pointInTime') {
      const ptStrat = strategy;
      return (
        <Spacing size={'sm'} direction={'horizontal'}>
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={'Date Column'}
          />
          <SelectBoxWithSearch
            type={'single'}
            setSelected={(value) => {
              setStrategy({
                ...ptStrat,
                dateColumn: value,
              });
            }}
            selected={strategy.dateColumn}
            list={possibleDateColumnsDropdowns}
            label={null}
          />
        </Spacing>
      );
    } else if (strategy.type === 'accumulating') {
      const accStrat = strategy;
      return (
        <Spacing size={'sm'} direction={'vertical'}>
          <Spacing size={'sm'} direction={'horizontal'}>
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={'Start Column'}
            />
            <SelectBoxWithSearch
              type={'single'}
              setSelected={(value) => {
                setStrategy({
                  ...accStrat,
                  startDateColumn: value,
                });
              }}
              selected={strategy.startDateColumn}
              list={possibleDateColumnsDropdowns}
              label={null}
            />
          </Spacing>
          <Spacing size={'sm'} direction={'horizontal'}>
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={'End Column'}
            />
            <SelectBoxWithSearch
              type={'single'}
              setSelected={(value) => {
                setStrategy({
                  ...accStrat,
                  endDateColumn: value,
                });
              }}
              selected={strategy.endDateColumn}
              list={possibleDateColumnsDropdowns}
              label={null}
            />
          </Spacing>
        </Spacing>
      );
    } else {
      throw guardExhaustive(strategy);
    }
  }, [strategy, setStrategy]);
  return (
    <Spacing size={'sm'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        setSelected={(value) => {
          if (value === 'accumulating') {
            setStrategy({
              type: 'accumulating',
              startDateColumn: '',
              endDateColumn: '',
            });
          } else if (value === 'pointInTime') {
            setStrategy({
              type: 'pointInTime',
              dateColumn: '',
            });
          } else {
            throw guardExhaustive(value);
          }
        }}
        selected={strategy.type}
        list={byMetricTrendsMetricBucketingStrategyDropdownValues}
        label={null}
      />
      {detailsUi}
    </Spacing>
  );
};

export const ByMetricTrendsConfigurationComponent = ({
  possibleDimensionColumns,
  possibleDateColumns,
  metricDefinitions,
  byMetricTrendsConfiguration,
  setByMetricTrendsConfiguration,
}: {
  possibleDimensionColumns: string[];
  possibleDateColumns: string[];
  metricDefinitions: MetricDefinition[];
  byMetricTrendsConfiguration: ByMetricTrendsConfig;
  setByMetricTrendsConfiguration: Setter<ByMetricTrendsConfig>;
}) => {
  const groupByConfigs = useMemo(() => {
    if (!byMetricTrendsConfiguration.isEnabled) {
      return [];
    }

    return byMetricTrendsConfiguration.groupByConfigs;
  }, [byMetricTrendsConfiguration]);

  const metricConfigs = useMemo(() => {
    if (!byMetricTrendsConfiguration.isEnabled) {
      return [];
    }

    return byMetricTrendsConfiguration.metrics;
  }, [byMetricTrendsConfiguration]);

  const setGroupByConfigs = useCallback(
    (groupByConfigs: ByMetricTrendsGroupByConfiguration[]) => {
      if (!byMetricTrendsConfiguration.isEnabled) {
        return;
      }

      setByMetricTrendsConfiguration({
        ...byMetricTrendsConfiguration,
        groupByConfigs: groupByConfigs,
      });
    },
    [byMetricTrendsConfiguration, setByMetricTrendsConfiguration],
  );

  const setMetricConfigs = useCallback(
    (metricConfigs: ByMetricTrendsMetricConfiguration[]) => {
      if (!byMetricTrendsConfiguration.isEnabled) {
        return;
      }

      setByMetricTrendsConfiguration({
        ...byMetricTrendsConfiguration,
        metrics: metricConfigs,
      });
    },
    [byMetricTrendsConfiguration, setByMetricTrendsConfiguration],
  );

  const groupByConfigsCrudOperations = useCrudOperations({
    items: groupByConfigs,
    setItems: setGroupByConfigs,
  });

  const listOptionsForGroupBy: {
    optionLabel: string;
    optionValue: string;
  }[] = useMemo(() => {
    return possibleDimensionColumns.map((column) => ({
      optionLabel: column,
      optionValue: column,
    }));
  }, [possibleDimensionColumns]);

  const metricConfigViewModels: MetricConfigViewModel[] = useMemo(() => {
    const currentMetricConfigs: MetricConfigViewModel[] = metricConfigs.map(
      (config) => {
        const foundMetricDefinition = metricDefinitions.find(
          (metricDefinition) =>
            metricDefinition.columnHeaderValue === config.metric,
        );

        if (!foundMetricDefinition) {
          return {
            metric: config.metric,
            isEnabled: true,
            errorMessages: [
              `Metric ${config.metric} not found in metric definitions.`,
            ],
            availableGroupBys: config.availableGroupBys,
            bucketingStrategy: config.bucketingStrategy,
          };
        }

        return {
          metric: config.metric,
          isEnabled: true,
          errorMessages: [],
          availableGroupBys: config.availableGroupBys,
          bucketingStrategy: config.bucketingStrategy,
        };
      },
    );

    const metricConfigsNotFound: MetricConfigViewModel[] = metricDefinitions
      .filter(
        (metricDefinition) =>
          !currentMetricConfigs.some(
            (config) => config.metric === metricDefinition.columnHeaderValue,
          ),
      )
      .map((metricDefinition) => ({
        metric: metricDefinition.columnHeaderValue,
        isEnabled: false,
        errorMessages: [],
        availableGroupBys: [],
        bucketingStrategy: null,
      }));

    return [...currentMetricConfigs, ...metricConfigsNotFound].sort((a, b) =>
      a.metric.localeCompare(b.metric),
    );
  }, [metricConfigs, metricDefinitions]);

  const setMetricConfigViewModels = useCallback(
    (newViewModels: MetricConfigViewModel[]) => {
      const newMetricConfigs: ByMetricTrendsMetricConfiguration[] = [];
      for (const viewModel of newViewModels) {
        if (viewModel.isEnabled) {
          newMetricConfigs.push({
            metric: viewModel.metric,
            availableGroupBys: viewModel.availableGroupBys,
            bucketingStrategy: viewModel.bucketingStrategy ?? {
              type: 'pointInTime',
              dateColumn: '',
            },
            isEnabled: viewModel.isEnabled,
          });
        }
      }

      setMetricConfigs(newMetricConfigs);
    },
    [setMetricConfigs],
  );

  const metricConfigViewModelCrudOperations = useCrudOperations({
    items: metricConfigViewModels,
    setItems: setMetricConfigViewModels,
  });

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <Box as={'div'} display={'flex'} alignItems={'center'}>
        <Spacing size={'md'} direction={'vertical'}>
          <Switch
            checked={byMetricTrendsConfiguration.isEnabled}
            onChange={() => {
              if (byMetricTrendsConfiguration.isEnabled) {
                setByMetricTrendsConfiguration({
                  isEnabled: false,
                });
              } else {
                setByMetricTrendsConfiguration({
                  isEnabled: true,
                  isEventStudyAnalysisEnabled: false,
                  metrics: [],
                  groupByConfigs: [],
                });
              }
            }}
          >
            Enable Outcome Trends
          </Switch>
          {byMetricTrendsConfiguration.isEnabled && (
            <Switch
              checked={
                byMetricTrendsConfiguration.isEventStudyAnalysisEnabled ?? false
              }
              onChange={() => {
                setByMetricTrendsConfiguration({
                  ...byMetricTrendsConfiguration,
                  isEventStudyAnalysisEnabled:
                    !byMetricTrendsConfiguration.isEventStudyAnalysisEnabled,
                });
              }}
            >
              Enable Event Study Analysis
            </Switch>
          )}
        </Spacing>
      </Box>

      {byMetricTrendsConfiguration.isEnabled && (
        <Spacing size={'sm'} direction={'vertical'}>
          <Spacing
            size={'sm'}
            direction={'horizontal'}
            as={'div'}
            className={'flex items-center'}
          >
            <S2Typography
              variant={'headline-6-alt'}
              translatedContent={'Group By Options'}
            />

            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  groupByConfigsCrudOperations.onAdd({
                    key: '',
                    displayName: '',
                  });
                },
              }}
              text={'Add'}
              variant={'outline'}
              size={'small'}
            />
          </Spacing>

          <Table
            columnDefinitions={[
              {
                id: 'column',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Column'}
                  />
                ),
                cell: ({ item, rowIndex }) => (
                  <SelectBoxWithSearch
                    type={'single'}
                    setSelected={(value) => {
                      groupByConfigsCrudOperations.onEdit(rowIndex, {
                        key: value,
                      });
                    }}
                    selected={item.key}
                    list={listOptionsForGroupBy}
                    label={null}
                  />
                ),
              },
              {
                id: 'displayName',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Display Name'}
                  />
                ),
                cell: ({ item, rowIndex }) => (
                  <input
                    className="form-input"
                    type="text"
                    value={item.displayName}
                    onChange={(e) =>
                      groupByConfigsCrudOperations.onEdit(rowIndex, {
                        displayName: e.target.value,
                      })
                    }
                  />
                ),
              },
              {
                id: 'actions',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Actions'}
                  />
                ),
                cell: ({ rowIndex }) => (
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () =>
                        groupByConfigsCrudOperations.onDelete(rowIndex),
                    }}
                    text={'Delete'}
                    variant={'destructive'}
                    size={'small'}
                  />
                ),
              },
            ]}
            items={groupByConfigs}
          />

          <S2Typography
            variant={'headline-6-alt'}
            translatedContent={'Outcome Trends Config'}
          />

          <Table
            columnDefinitions={[
              {
                id: 'isEnabled',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Is Enabled'}
                  />
                ),
                cell: ({ item, rowIndex }) => (
                  <Switch
                    checked={item.isEnabled}
                    onChange={() => {
                      metricConfigViewModelCrudOperations.onEdit(rowIndex, {
                        isEnabled: !item.isEnabled,
                      });
                    }}
                  />
                ),
              },
              {
                id: 'metric',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Metric'}
                  />
                ),
                cell: ({ item }) => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={item.metric}
                  />
                ),
              },
              {
                id: 'availableGroupBys',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Available Group Bys'}
                  />
                ),
                cell: ({ item, rowIndex }) => {
                  return item.isEnabled ? (
                    <ByMetricTrendsConfigurationAvailableGroupBysComponent
                      availableGroupBys={item.availableGroupBys}
                      groupByConfigs={groupByConfigs}
                      setAvailableGroupBys={(newAvailableGroupBys) => {
                        metricConfigViewModelCrudOperations.onEdit(rowIndex, {
                          availableGroupBys: newAvailableGroupBys,
                        });
                      }}
                    />
                  ) : (
                    <div>N/A</div>
                  );
                },
              },
              {
                id: 'bucketingStrategy',
                header: () => (
                  <S2Typography
                    variant={'headline-7-alt'}
                    translatedContent={'Bucketing Strategy'}
                  />
                ),
                cell: ({ item, rowIndex }) => {
                  return item.isEnabled && item.bucketingStrategy ? (
                    <ByMetricTrendsBucketingStrategyComponent
                      possibleDateColumns={possibleDateColumns}
                      strategy={item.bucketingStrategy}
                      setStrategy={(newStrategy) => {
                        metricConfigViewModelCrudOperations.onEdit(rowIndex, {
                          bucketingStrategy: newStrategy,
                        });
                      }}
                    />
                  ) : (
                    <div>N/A</div>
                  );
                },
              },
            ]}
            items={metricConfigViewModels}
          />
        </Spacing>
      )}
    </Spacing>
  );
};
